import { LTextareaField, LDivider, LColumns, LColumn, LFlex } from "@components";
import { DefaultErrorMessages } from "@helpers/formValidationHelper";
import { OrganizationStatus } from "@helpers/organizationHelper";
import { yupResolver } from "@hookform/resolvers/yup";
import { UserStatus } from "@store/generalTypes";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { number, object, SchemaOf, string } from "yup";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LBtn } from "../../../common/components/LBtn";
import { InputFieldProps, LInputField } from "../../../common/components/LInputField";
import { LInputRow } from "../../../common/components/LInputRow";
import { NavItems } from "../../../common/helpers/navigation";
import { updateUser } from "../../../common/store/userInfoSlice";
import { useApi } from "../../../services/useApi";
import { AssessmentQuantity } from "../components/AssessmentQuantity";
// TODO: should move select options to other folder?
import { CountrySelectOptions } from "../../signup/components/CountrySelectOptions";
import { TitleSelectOptions } from "../../signup/components/TitleSelectOptions";

export interface OrganizationForm {
    email: string;
    orgName: string;
    country?: string;
    otherCountry?: string;
    details?: string;
    phoneNumber: string;
    title?: string;
    firstName: string;
    lastName: string;
    jobTitle: string;
    otherJobTitle?: string;
    assessmentQuantity: number;
    unitPrice: number;
    discount?: number;
}

const formSchema: SchemaOf<OrganizationForm> = object({
    email: string().email().required(DefaultErrorMessages.required),
    orgName: string().required(DefaultErrorMessages.required),
    country: string().required(DefaultErrorMessages.required),
    otherCountry: string().when("country", (val, schema) => {
        return val === "Other" ? schema.required("Other country is required") : schema;
    }),
    details: string().optional(),
    phoneNumber: string()
        .required(DefaultErrorMessages.required)
        .matches(/^\d+$/, DefaultErrorMessages.numeric("Phone number"))
        .length(10, DefaultErrorMessages.lengthDigits(10, "Phone number")),
    title: string().optional(),
    firstName: string().required(DefaultErrorMessages.required),
    lastName: string().required(DefaultErrorMessages.required),
    jobTitle: string().required(DefaultErrorMessages.required),
    otherJobTitle: string().when("jobTitle", (val, schema) => {
        return val === "Other" ? schema.required("Other job title is required") : schema;
    }),
    assessmentQuantity: number()
        .required(DefaultErrorMessages.required)
        .typeError(DefaultErrorMessages.required)
        .min(0),
    unitPrice: number().required(DefaultErrorMessages.required).typeError(DefaultErrorMessages.required).min(0),
    discount: number()
        .typeError(DefaultErrorMessages.numeric())
        .moreThan(0)
        .lessThan(100)
        .transform((v, o) => (Number(o) ? v : undefined))
});

export const CreateOrganization = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const validateMethods = useForm<OrganizationForm>({
        resolver: yupResolver(formSchema)
    });

    const { createUser, createOrganization, createPurchase, loading } = useApi();

    const user = useAppSelector((state) => state.user);

    const formSubmitHandler: SubmitHandler<OrganizationForm> = async (data: OrganizationForm) => {
        const createdUser = await createUser({
            email: data.email,
            firstName: data.firstName,
            jobTitle: data.jobTitle,
            lastName: data.lastName,
            status: UserStatus.Inactive,
            otherJobTitle: data.otherJobTitle,
            password: "Lassi2023",
            title: data.title
        });

        dispatch(updateUser(createdUser));

        const org = await createOrganization({
            orgName: data.orgName,
            status: OrganizationStatus.active,
            details: data.details,
            primaryContact: {
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                phoneNumber: data.phoneNumber
            },
            userId: createdUser.id,
            address: {
                city: "",
                postalCode: "",
                stateCode: "",
                streetAddress: "",
                country: data.country === "Other" ? data.otherCountry : data.country,
                streetAddress2: ""
            }
        });

        await createPurchase(org.id!, data.assessmentQuantity, true, data.unitPrice, data.discount);

        navigate(NavItems.SuperAdmin.Landing.path);
    };

    const nameGroup = [
        {
            type: "text",
            label: "Title",
            inputName: "title",
            isSmall: true,
            defaultValue: user.title,
            value: user.title
        } as InputFieldProps,
        {
            type: "text",
            label: "First Name",
            inputName: "firstName",
            placeholder: "e.g. John",
            isExpanded: true,
            defaultValue: user.firstName,
            value: user.firstName
        } as InputFieldProps,
        {
            type: "text",
            label: "Last Name",
            inputName: "lastName",
            placeholder: "e.g. Smith",
            isExpanded: true,
            defaultValue: user.lastName,
            value: user.lastName
        } as InputFieldProps
    ];

    const handleOnCancel = (): void => {
        navigate(NavItems.SuperAdmin.Landing.path);
    };

    return (
        <div className="mx-6">
            <FormProvider {...validateMethods}>
                <h1 className="title is-4 mt-5 has-text-dark-gray">Organization Details</h1>
                <form onSubmit={validateMethods.handleSubmit(formSubmitHandler)}>
                    <LDivider title="Organization" isFat />
                    <div className="my-5 mb-6">
                        <LColumns>
                            <LColumn size="6">
                                <LInputField
                                    label="Organization name"
                                    inputName="orgName"
                                    placeholder="Enter organization name"
                                    defaultValue={""}
                                    value={""}
                                />
                            </LColumn>
                            <LColumn size="6">
                                <CountrySelectOptions value={""} otherCountry={""} />
                            </LColumn>
                        </LColumns>
                        <LTextareaField
                            label="Additional details (optional)"
                            inputName="details"
                            placeholder="Add details"
                            defaultValue={""}
                            value={""}
                        />
                    </div>
                    <LDivider title="Primary Contact" isFat />
                    <div className="my-5 mb-6">
                        <LInputRow inputs={nameGroup} />
                        <TitleSelectOptions value={""} otherJobTitle={""} />
                        <LColumns>
                            <LColumn size="3">
                                <LInputField
                                    type="email"
                                    label="Organization email"
                                    inputName="email"
                                    placeholder="Enter organization email"
                                    defaultValue={""}
                                    value={""}
                                />
                            </LColumn>
                            <LColumn size="3">
                                <LInputField
                                    label="Phone Number"
                                    inputName="phoneNumber"
                                    placeholder="Enter phone number"
                                    defaultValue={""}
                                    maxLength={10}
                                />
                            </LColumn>
                        </LColumns>
                    </div>
                    <LDivider title="LASSI-D™ Assessments" isFat />
                    <div className="my-5 mb-6">
                        <AssessmentQuantity control={validateMethods.control} />
                    </div>

                    {/* TODO: Add different administrator? */}
                    {/* <LDivider title="Organization Administrator" isFat />
                    <span>
                        This person is repsonsible for adding and configuring staff and locations for this organization
                        or assigning someone else at their organization to do so
                    </span> */}
                    <LFlex alignCenter row className="pt-3 my-6">
                        <LBtn noSpacing type="submit" loading={loading} isBold>
                            Create Organization
                        </LBtn>
                        <LBtn variation="unstyled" classes="has-text-weight-bold" onClick={handleOnCancel}>
                            Cancel
                        </LBtn>
                    </LFlex>
                </form>
            </FormProvider>
        </div>
    );
};
